import { useMultipleSelectCount } from '@/components/selects/Select/useMultipleSelectCount'
import type { SelectProps as AntdSelectProps, RefSelectProps } from 'antd'
import { Select as AntdSelect } from 'antd'
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react'
import styles from './styles.module.scss'

export type SelectRefType = RefSelectProps & {
  change: (value: unknown) => void
  value: string | string[]
}

export interface OptionProps {
  label: React.ReactNode
  value: string
  selectedStateLabel?: string
  disabled?: boolean
}

type SelectOnlyOnCloseType =
  | {
      selectOnlyOnClose?: false
      onClose?: never
    }
  | {
      selectOnlyOnClose: true
      onClose: () => void
    }

type MultipleSelectWithCountType =
  | {
      mode: 'multiple' | 'tags'
      countLabel?: string
    }
  | {
      mode?: undefined
      countLabel?: never
    }

export type SelectProps = Omit<AntdSelectProps, 'options'> & {
  options: OptionProps[]
  shouldFixFloatingMenu?: boolean
  rounded?: boolean
  stayActive?: boolean
  minWidth?: string
  selectedOnTop?: boolean
  overridePlaceholderStyle?: boolean
} & SelectOnlyOnCloseType &
  MultipleSelectWithCountType

// https://github.com/ant-design/ant-design/issues/8268#issuecomment-438419077
const fixFloatingMenuProps: Pick<SelectProps, 'getPopupContainer'> = {
  getPopupContainer: (trigger) => trigger.parentNode,
}

export const Select = forwardRef<SelectRefType, SelectProps>(
  (
    {
      dropdownMatchSelectWidth = true,
      getPopupContainer,
      options,
      shouldFixFloatingMenu = true,
      selectOnlyOnClose = false,
      onClose,
      onDropdownVisibleChange,
      onDeselect,
      className,
      rounded,
      stayActive,
      value,
      size,
      countLabel,
      minWidth = '9rem',
      style,
      selectedOnTop = true,
      overridePlaceholderStyle = false,
      ...props
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false)
    const innerRef = useRef<RefSelectProps>(null)
    const { className: countLabelClassName, ...multipleSelectCountProps } = useMultipleSelectCount({
      countLabel,
      value,
    })

    const optionsList = useMemo(() => {
      if (!selectedOnTop || !value || (typeof value !== 'string' && !Array.isArray(value)))
        return options

      const selectedSet = new Set(value)
      const selectedOptionsOnTop = options.toSorted((a, b) => {
        const aSelected = selectedSet.has(a.value) ? 0 : 1
        const bSelected = selectedSet.has(b.value) ? 0 : 1
        return aSelected - bSelected
      })
      return selectedOptionsOnTop
    }, [options, selectedOnTop, value])

    useImperativeHandle(ref, () => {
      if (!innerRef.current) {
        return {} as SelectRefType
      }

      return {
        ...innerRef.current,
        change: (value) => {
          props.onChange?.(value, optionsList)
        },
        value,
      }
    })

    const popupContainerProps = shouldFixFloatingMenu ? fixFloatingMenuProps : { getPopupContainer }

    const handleSelectOpen: SelectProps['onDropdownVisibleChange'] = (open) => {
      if (!selectOnlyOnClose) {
        return onDropdownVisibleChange?.(open)
      }

      const isClosingSelect = !open
      isClosingSelect && onClose?.()

      setIsOpen(open)
      onDropdownVisibleChange?.(open)
    }

    const handleDeselect: SelectProps['onDeselect'] = (value, option) => {
      if (!selectOnlyOnClose) {
        return onDeselect?.(value, option)
      }
      !isOpen && onDeselect?.(value, option)
    }

    const selectOptionLabelProp: Pick<SelectProps, 'optionLabelProp'> = options.some(
      (option) => option.selectedStateLabel,
    )
      ? { optionLabelProp: 'selectedStateLabel' }
      : {}

    const roundedClassName = rounded ? styles.rounded : ''
    const placeholderClassName = overridePlaceholderStyle ? styles.overridePlaceholder : ''
    // * Active state is only for single selects
    const activeStateClassName = stayActive && !!value ? styles.activeState : ''
    const classNames = [
      styles.root,
      roundedClassName,
      activeStateClassName,
      countLabelClassName,
      placeholderClassName,
      className,
    ].join(' ')
    // Required when shouldFixFloatingMenu, because it renders in a portal
    const popupClassName = [
      styles.root,
      props.mode === 'multiple' ? 'ant-select-multiple' : '',
    ].join(' ')

    return (
      <AntdSelect
        {...popupContainerProps}
        {...selectOptionLabelProp}
        {...multipleSelectCountProps}
        {...props}
        className={classNames}
        popupClassName={popupClassName}
        ref={innerRef}
        dropdownMatchSelectWidth={dropdownMatchSelectWidth}
        onDropdownVisibleChange={handleSelectOpen}
        onDeselect={handleDeselect}
        value={value}
        size={size}
        style={{ minWidth, ...style }}
        options={optionsList}
      />
    )
  },
)
Select.displayName = 'Select'
